import { Box, Tooltip, Typography } from '@mui/material'
import { FC } from 'react'
import { useTranslate } from 'react-admin'

import type { AisMark, GanttBar, StayDate, StayDates } from '@/data/source'
import { formatShortDateTime } from '@/domain/dateTime'

import { aisDifferenceColor, useAisDifference } from './useAisDifference'

type Side = 'arrival' | 'departure'

const AisTime: FC<{ side: Side; mark: AisMark; id: string }> = ({ side, mark, id }) => {
  const translate = useTranslate()
  const { signed, meaning } = useAisDifference()
  const compared = mark.differenceS !== null && mark.level !== null
  return (
    <Typography variant="caption" display="block" pl={1} data-testid={`stay-ais-${side}-${id}`}>
      {translate('pages.scales.timeline.stay_times.ais')} {formatShortDateTime(mark.at)}
      {compared && mark.differenceS !== null && mark.level !== null ? (
        <Tooltip
          title={`${translate('pages.scales.timeline.ais.difference', { difference: signed(mark.differenceS) })}, ${meaning(mark.level)}`}
        >
          <Box
            component="span"
            ml={0.5}
            fontWeight="bold"
            whiteSpace="nowrap"
            color={aisDifferenceColor(mark.level)}
            data-testid={`stay-ais-${side}-difference-${id}`}
            data-level={mark.level}
          >
            <Box component="span" aria-hidden="true">
              ●{' '}
            </Box>
            {signed(mark.differenceS)}
          </Box>
        </Tooltip>
      ) : (
        <Box component="span" ml={0.5} color="text.secondary" fontStyle="italic" data-testid={`stay-ais-${side}-not-compared-${id}`}>
          {translate('pages.scales.timeline.stay_times.not_compared')}
        </Box>
      )}
    </Typography>
  )
}

const SostratTime: FC<{ side: Side; date: StayDate; unconfirmed: boolean; id: string }> = ({ side, date, unconfirmed, id }) => {
  const translate = useTranslate()
  const actual = date.actual !== null
  const at = date.actual ?? date.planned
  const basis = actual ? 'actual' : side === 'departure' && unconfirmed ? 'unconfirmed' : 'planned'
  return (
    <Typography variant="caption" display="block" color="text.secondary" data-testid={`stay-sostrat-${side}-${id}`} data-basis={basis}>
      {translate(`pages.scales.timeline.stay_times.${side}`)}{' '}
      <Box component="span" color="text.primary">
        {at === null ? '…' : formatShortDateTime(at)}
      </Box>{' '}
      <Box component="span" fontStyle={actual ? undefined : 'italic'}>
        ({translate(`pages.scales.timeline.stay_times.basis.${basis}`)})
      </Box>
    </Typography>
  )
}

export const StayTimes: FC<{ bar: GanttBar; dates: StayDates | undefined }> = ({ bar: { marker, ais }, dates }) => {
  const translate = useTranslate()
  if (marker.type !== 'PLACE' || dates === undefined) return null
  return (
    <Box mt={0.25} data-testid={`stay-times-${marker.id}`}>
      <SostratTime side="arrival" date={dates.arrival} unconfirmed={dates.unconfirmed} id={marker.id} />
      {ais && <AisTime side="arrival" mark={ais.arrival} id={marker.id} />}
      <SostratTime side="departure" date={dates.departure} unconfirmed={dates.unconfirmed} id={marker.id} />
      {ais?.departure && <AisTime side="departure" mark={ais.departure} id={marker.id} />}
      {ais?.otherMuelle && (
        <Typography variant="caption" display="block" color="warning.main" data-testid={`stay-ais-muelle-${marker.id}`}>
          {translate('pages.scales.timeline.ais.other_muelle', { muelle: ais.otherMuelle })}
        </Typography>
      )}
    </Box>
  )
}
